import L from 'leaflet'
import 'leaflet.markercluster'
import 'leaflet.markercluster/dist/MarkerCluster.css'
import 'leaflet.markercluster/dist/MarkerCluster.Default.css'
import gsap from 'gsap'
import { Carousel, Fancybox } from '@fancyapps/ui'

import loadKml from './modules/kml-loader.js'
import createPopup from './modules/create-popup.js'
import createDropdownFilter from './modules/create-dropdown-filter.js'
import DropdownFilter from '../../../public-src/js/modules/dropdown-filter.js'
import getFormattedFilename from './modules/formatted-filename'
import getPluralName from './modules/plural-names'
import getCategoryDisplayName from './config/category-display-names'
import { initClusterTooltip } from './modules/cluster-tooltip.js'
import { loadRouteKmlLayer } from './modules/route-kml-loader.js'
import { initWalkthrough } from './modules/walkthrough.js'

import settings from './config/settings'
import places from './config/places'
import { initDummyData, setupDummyRegionsLayer, shouldFetchRegions } from './modules/dummy-data.js'

// Groups categories for display in the legend with section titles
const CATEGORY_GROUPS = {
  Bezoeken: ['Natuurgebied', 'Parkeerplaats', 'Project', 'Infopunt', 'Horeca', 'Erfgoed', 'Uitkijkpunt', '360° view'],
  Routes: ['Wandelroute', 'Fietsroute', 'Ruiterpad', 'Rolstoel- en kinderwagenroute'],
  'Vaste activiteiten': ['Activiteit', 'Kinderactiviteit'],
  Vrijwilligersgroepen: [
    'Weidevogelbescherming',
    'Uilenbescherming',
    'Erfvogelbescherming',
    'Vleermuizenbescherming',
    'Natuur- & Landschapbescherming',
    'Soorten monitoring',
    'Gidsen & Voorlichting',
    'Terreinen Brabants Landschap',
  ],
}

class MapApp {
  constructor() {
    this.mapSection = document.querySelector('section.map')

    if (!this.mapSection) return

    // Determine map type to use different data sources and settings
    this.mapType = this.mapSection.dataset.type == 'VolunteerGroupMapPage' ? 'volunteerGroups' : 'default'

    this.feed = this.mapType == 'volunteerGroups' ? '/vrijwilligersgroepen' : '/api'
    this.feedSegment = this.mapType == 'volunteerGroups' ? 'volunteerGroups' : 'poi'
    this.settings = this.mapType == 'volunteerGroups' ? settings.volunteerGroups : settings.default

    this.baseHref = window.location.href.split('?')[0]

    if (this.baseHref.endsWith('/')) {
      this.baseHref = this.baseHref.slice(0, -1)
    }

    this.lastSegment = this.baseHref.substring(this.baseHref.lastIndexOf('/') + 1)

    this.params = new URLSearchParams(window.location.href.split('?')[1])
    this.params.delete('action_zoeken')

    this.center = [51.73379, 5.13768]
    this.zoom = 10

    // Layer groups for category-based filtering (markers stored separately in mainClusterGroup)
    this.markerLayerGroups = {}

    const createClusterIcon = (cluster) => {
      const count = cluster.getChildCount()

      return L.divIcon({
        html: `<div class="marker-cluster"><span>${count}</span></div>`,
        className: 'marker-cluster-container',
        iconSize: L.point(40, 40),
      })
    }

    // Cross-category clustering: all markers in one cluster group regardless of category
    this.mainClusterGroup = L.markerClusterGroup({
      iconCreateFunction: createClusterIcon,
      maxClusterRadius: 80,
      spiderfyOnMaxZoom: true,
      showCoverageOnHover: false,
      zoomToBoundsOnClick: true,
    })

    if (this.mapType == 'default') {
      this.markerLayerGroups = {
        Natuurgebied: L.layerGroup(),
        Infopunt: L.layerGroup(),
        Horeca: L.layerGroup(),
        Erfgoed: L.layerGroup(),
        Uitkijkpunt: L.layerGroup(),
        Parkeerplaats: L.layerGroup(),
        Wandelroute: L.layerGroup(),
        Fietsroute: L.layerGroup(),
        Ruiterpad: L.layerGroup(),
        'Rolstoel- en kinderwagenroute': L.layerGroup(),
        Activiteit: L.layerGroup(),
        Project: L.layerGroup(),
        Kinderactiviteit: L.layerGroup(),
        '360° view': L.layerGroup(),
      }
    }

    if (this.mapType == 'volunteerGroups') {
      this.markerLayerGroups = {
        Weidevogelbescherming: L.layerGroup(),
        Uilenbescherming: L.layerGroup(),
        'Natuur- & Landschapbescherming': L.layerGroup(),
        Erfvogelbescherming: L.layerGroup(),
        Vleermuizenbescherming: L.layerGroup(),
        'Soorten monitoring': L.layerGroup(),
        'Gidsen & Voorlichting': L.layerGroup(),
        'Terreinen Brabants Landschap': L.layerGroup(),
      }
    }

    this.markerIcons = {}

    this.userLocation = false

    this.currentMarker = false
    this.currentRegion = false

    // Loading state tracking
    this.loader = document.getElementById('map-loader')
    this.loadingPromises = []
    this.isLoading = true
    this.markersLoaded = false
    this.regionsLoaded = false

    this.aside = document.querySelector('.map__aside')

    if (this.aside) {
      this.asideCloseButton = this.aside.querySelector('.js-aside-close')
      this.asideContainer = this.aside.querySelector('.js-container')
      this.asideCurrent = false
    }

    L.Icon.Default.imagePath = '_resources/ontdekdenatuurmodule/img/leaflet/'

    gsap.defaults({
      ease: 'power1.out',
      duration: 0.2,
    })

    initWalkthrough()
    initDummyData()
    this.initMap()
    this.initAside()

    this.getMarkerData()
    this.getRegionData()

    // Track all loading promises
    this.loadingPromises.push(this.getMarkerDataPromise)

    if (this.getRegionDataPromise) {
      this.loadingPromises.push(this.getRegionDataPromise)
    } else {
      // If regions aren't needed, mark as loaded immediately
      this.regionsLoaded = true
    }

    // Only wait for region data if it exists
    const promisesToWait = [this.getMarkerDataPromise]
    if (this.getRegionDataPromise) {
      promisesToWait.push(this.getRegionDataPromise)
    }

    Promise.all(promisesToWait).then((values) => {
      this.setRegionSearchField()
    })

    // Wait for all data and KML layers to load before hiding loader
    this.waitForAllDataToLoad()

    if (this.params.get('omgeving')) {
      this.goToPlace(this.params.get('omgeving'))
    }
  }

  initMap() {
    this.map = L.map('map', {
      zoomSnap: 0.5,
      zoomControl: false,
      doubleClickZoom: false,
    })

    this.map.addEventListener('zoomend', () => this.onZoomLevelChanged())

    this.map.setView(this.center, this.zoom)

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
    }).addTo(this.map)

    L.control.zoom({ position: 'topright' }).addTo(this.map)

    this.mainClusterGroup.addTo(this.map)
    initClusterTooltip(this.mainClusterGroup, this.map, this.mapType, getFormattedFilename)

    this.setPropertiesKmlLayer()
    this.setWalkingRoutesKmlLayer()
    this.setCyclingRoutesKmlLayer()
    this.setRidersRoutesKmlLayer()
    this.setWheelchairRoutesKmlLayer()

    this.createFiltersContainer()

    this.setPlacesSelect()
    this.setUserLocationControl()
  }

  createFiltersContainer() {
    const self = this

    this.createMobileFilterButton()

    L.Control.FiltersContainer = L.Control.extend({
      onAdd: function (map) {
        const container = document.createElement('div')
        container.classList.add('leaflet-control', 'filters-container')
        container.setAttribute('data-expanded', 'false')

        const header = document.createElement('button')
        header.classList.add('filters-container__header')
        header.innerHTML =
          '<span class="filters-container__title">Filters</span><span class="filters-container__toggle"><img src="/_resources/ontdekdenatuurmodule/img/leaflet/chevron-down.svg" alt="Chevron down" /></span>'

        header.addEventListener('click', () => {
          const isExpanded = container.getAttribute('data-expanded') === 'true'
          const newExpandedState = !isExpanded
          container.setAttribute('data-expanded', newExpandedState)
          
          // Handle map interactions on mobile
          const isMobile = window.matchMedia('(max-width: 480px)').matches
          if (isMobile) {
            if (newExpandedState) {
              // Disable map interactions when filters are expanded
              map.dragging.disable()
              map.touchZoom.disable()
              map.doubleClickZoom.disable()
              map.scrollWheelZoom.disable()
              
              // Prevent touch events from propagating to the map
              L.DomEvent.on(container, 'touchstart touchmove touchend', L.DomEvent.stopPropagation)
            } else {
              // Re-enable map interactions when filters are closed
              map.dragging.enable()
              map.touchZoom.enable()
              map.doubleClickZoom.enable()
              map.scrollWheelZoom.enable()
              
              // Remove touch event prevention
              L.DomEvent.off(container, 'touchstart touchmove touchend', L.DomEvent.stopPropagation)
            }
          }
        })

        const content = document.createElement('div')
        content.classList.add('filters-container__content')

        const closeButton = document.createElement('button')
        closeButton.classList.add('filters-container__close')
        closeButton.innerHTML =
          '<img src="/_resources/ontdekdenatuurmodule/img/leaflet/filter-close.svg" alt="Close" />'
        closeButton.addEventListener('click', () => {
          container.setAttribute('data-expanded', 'false')
          if (self.mobileFilterButton) {
            self.mobileFilterButton.setAttribute('data-active', 'false')
          }
          const mapContainer = self.mapSection || map.getContainer().closest('.map') || map.getContainer()
          if (mapContainer) {
            mapContainer.classList.remove('filters-overlay-active')
            
            // Re-enable map interactions when filters are closed on mobile
            const isMobile = window.matchMedia('(max-width: 480px)').matches
            if (isMobile) {
              map.dragging.enable()
              map.touchZoom.enable()
              map.doubleClickZoom.enable()
              map.scrollWheelZoom.enable()
              
              // Remove touch event prevention
              L.DomEvent.off(container, 'touchstart touchmove touchend', L.DomEvent.stopPropagation)
            }
          }
        })

        const bottomSection = document.createElement('div')
        bottomSection.classList.add('filters-container__bottom')

        const resetButton = document.createElement('button')
        resetButton.classList.add('filters-container__button', 'filters-container__button--reset')
        resetButton.innerHTML = 'Filters wissen'
        resetButton.addEventListener('click', () => {
          self.resetAllFilters()
        })

        const viewResultsButton = document.createElement('button')
        viewResultsButton.classList.add('filters-container__button', 'filters-container__button--view-results')
        viewResultsButton.innerHTML = 'Bekijk resultaten'
        viewResultsButton.addEventListener('click', () => {
          container.setAttribute('data-expanded', 'false')
          if (self.mobileFilterButton) {
            self.mobileFilterButton.setAttribute('data-active', 'false')
          }
          const mapContainer = self.mapSection || map.getContainer().closest('.map') || map.getContainer()
          if (mapContainer) {
            mapContainer.classList.remove('filters-overlay-active')
          }
        })

        bottomSection.appendChild(resetButton)
        bottomSection.appendChild(viewResultsButton)

        container.appendChild(header)
        container.appendChild(closeButton)
        container.appendChild(content)
        container.appendChild(bottomSection)

        self.filtersContainerContent = content
        self.filtersContainer = container

        return container
      },
    })

    L.control.FiltersContainer = function (opts) {
      return new L.Control.FiltersContainer(opts)
    }

    L.control.FiltersContainer({ position: 'topleft' }).addTo(this.map)
  }

  createMobileFilterButton() {
    const self = this

    L.Control.MobileFilterButton = L.Control.extend({
      onAdd: function (map) {
        const button = document.createElement('button')
        button.classList.add('leaflet-control', 'mobile-filter-button')
        button.setAttribute('data-active', 'false')
        button.innerHTML =
          '<img src="/_resources/ontdekdenatuurmodule/img/leaflet/filters.svg" alt="Filters" /> Filters'

        button.addEventListener('click', () => {
          const isActive = button.getAttribute('data-active') === 'true'
          const newActiveState = !isActive
          button.setAttribute('data-active', newActiveState)

          const filtersContainer = self.filtersContainer || document.querySelector('.filters-container')
          if (filtersContainer) {
            filtersContainer.setAttribute('data-expanded', newActiveState)

            // Toggle z-index to show filters overlay above other controls on mobile
            const mapContainer = self.mapSection || map.getContainer().closest('.map') || map.getContainer()
            if (mapContainer) {
              if (newActiveState) {
                mapContainer.classList.add('filters-overlay-active')
                
                // Disable map dragging when filters are expanded on mobile
                // This prevents the map from dragging when scrolling in dropdown lists
                const isMobile = window.matchMedia('(max-width: 480px)').matches
                if (isMobile) {
                  map.dragging.disable()
                  map.touchZoom.disable()
                  map.doubleClickZoom.disable()
                  map.scrollWheelZoom.disable()
                  
                  // Prevent touch events from propagating to the map
                  L.DomEvent.on(filtersContainer, 'touchstart touchmove touchend', L.DomEvent.stopPropagation)
                }
              } else {
                mapContainer.classList.remove('filters-overlay-active')
                
                // Re-enable map interactions when filters are closed
                const isMobile = window.matchMedia('(max-width: 480px)').matches
                if (isMobile) {
                  map.dragging.enable()
                  map.touchZoom.enable()
                  map.doubleClickZoom.enable()
                  map.scrollWheelZoom.enable()
                  
                  // Remove touch event prevention
                  L.DomEvent.off(filtersContainer, 'touchstart touchmove touchend', L.DomEvent.stopPropagation)
                }
              }
            }
          }
        })

        self.mobileFilterButton = button
        return button
      },
    })

    L.control.MobileFilterButton = function (opts) {
      return new L.Control.MobileFilterButton(opts)
    }

    L.control.MobileFilterButton({ position: 'topleft' }).addTo(this.map)
  }

  // Insert controls in correct order: dropdown (first), reset buttons (middle), layers (last)
  addControlToFiltersContainer(element, position) {
    if (!this.filtersContainerContent) return false

    if (position === 'first') {
      this.filtersContainerContent.insertBefore(element, this.filtersContainerContent.firstChild)
      return true
    } else if (position === 'middle') {
      const dropdown = this.filtersContainerContent.querySelector('#region-search-filter')?.closest('.leaflet-control')
      if (dropdown && dropdown.nextSibling) {
        this.filtersContainerContent.insertBefore(element, dropdown.nextSibling)
      } else if (dropdown) {
        this.filtersContainerContent.appendChild(element)
      } else {
        this.filtersContainerContent.insertBefore(element, this.filtersContainerContent.firstChild)
      }
      return true
    } else {
      this.filtersContainerContent.appendChild(element)
      return true
    }
  }

  onZoomLevelChanged() {
    if (!this.map) return
  }

  getMarkerData() {
    this.getMarkerDataPromise = new Promise(
      function (resolve, reject) {
        fetch(`${this.feed}/${this.feedSegment}`, {
          headers: {
            'x-requested-with': 'XMLHttpRequest',
          },
        })
          .then((response) => response.json())
          .then((data) => {
            this.poiArr = data
            if (this.poiArr.length) {
              this.initMarkers()

              let markerBasedOnParam = false
              let markerBasedOnLastSegment = false

              if (this.params.get('Regio')) {
                const found = this.poiArr.find((element) => element.slug === this.params.get('Regio'))
                markerBasedOnParam = found ? found : false
              }

              if (this.lastSegment) {
                const found = this.poiArr.find((element) => element.slug === this.lastSegment.replace(/\\/g, ''))
                markerBasedOnLastSegment = found ? found : false
              }

              // Handle URL-based marker selection (from params or URL segment)
              if (markerBasedOnParam) {
                this.currentMarker = markerBasedOnParam
                this.params.delete('Regio')
              } else if (markerBasedOnLastSegment) {
                this.currentMarker = markerBasedOnLastSegment
                this.baseHref = this.baseHref.replace(`/${this.lastSegment}`, '')
              }

              if (this.currentMarker) {
                this.centerMapOnCurrentPoi(this.currentMarker)
                this.onMarkerClick(this.currentMarker.id, 'poi', this.currentMarker)
              }
            } else {
              // No markers, mark as loaded
              this.markersLoaded = true
              this.checkIfAllDataLoaded()
            }

            resolve(this.poiArr)
          })
          .catch((error) => {
            console.error('Error:', error)
            reject('error')
          })
      }.bind(this)
    )
  }

  getPoiById(id) {
    return this.poiArr.find((element) => element.id === id)
  }

  waitForAllDataToLoad() {
    // Wait for all promises to resolve
    Promise.all(this.loadingPromises)
      .then(() => {
        // Give a small delay to ensure markers are rendered
        setTimeout(() => {
          this.checkIfAllDataLoaded()
        }, 100)
      })
      .catch((error) => {
        console.error('Error loading map data:', error)
        // Still try to hide loader after a timeout
        setTimeout(() => {
          this.checkIfAllDataLoaded()
        }, 2000)
      })
  }

  checkIfAllDataLoaded() {
    // Check if markers are loaded (either we have markers or the array is empty)
    const markersReady = this.markersLoaded || (this.poiArr && this.poiArr.length === 0)

    // Check if regions are loaded (either we have regions or we don't need them)
    const regionsReady = this.regionsLoaded || !shouldFetchRegions(this.mapSection)

    // If everything is ready and we're still loading, hide the loader
    if (this.isLoading && markersReady && regionsReady) {
      this.hideLoader()
    }
  }

  hideLoader() {
    if (!this.loader) return

    this.isLoading = false

    // Fade out the loader
    gsap.to(this.loader, {
      opacity: 0,
      duration: 0.3,
      ease: 'power1.out',
      onComplete: () => {
        if (this.loader) {
          this.loader.style.display = 'none'
        }
      },
    })
  }

  initAside() {
    if (!this.aside) return

    if (this.asideCloseButton) {
      this.asideCloseButton.addEventListener('click', () => this.closeAside())
    }
  }

  initMarkers() {
    if (!this.poiArr.length) {
      this.markersLoaded = true
      this.checkIfAllDataLoaded()
      return
    }

    for (let i = 0; i < this.poiArr.length; i++) {
      const poi = this.poiArr[i]
      const marker = L.marker([poi.lat, poi.lng])
      marker.id = poi.id
      marker.regionId = poi.regionId
      marker.category = poi.category
      marker.name = poi.name
      marker.slug = poi.slug
      marker.tour360Link = poi.tour360Link

      const self = this

      if (!this.markerLayerGroups.hasOwnProperty(poi.category)) {
        this.markerLayerGroups[poi.category] = L.layerGroup()
      }

      if (!this.markerIcons.hasOwnProperty(poi.category)) {
        let iconUrl
        if (this.mapType == 'volunteerGroups') {
          // Use PNG for volunteer groups (standard), except Terreinen Brabants Landschap which uses SVG
          if (poi.category === 'Terreinen Brabants Landschap') {
            iconUrl = `/_resources/ontdekdenatuurmodule/img/markers/${getFormattedFilename(poi.category)}-icon.svg?v=1`
          } else {
            iconUrl = `/_resources/ontdekdenatuurmodule/img/markers/${getFormattedFilename(poi.category)}-icon.png`
          }
        } else {
          iconUrl = `/_resources/ontdekdenatuurmodule/img/markers/${getFormattedFilename(poi.category)}.svg?v=1`
        }
        const displayName = getCategoryDisplayName(poi.category)

        const iconSize = 40
        this.markerIcons[poi.category] = L.divIcon({
          html: `<div class="marker-icon-wrapper"><img src="${iconUrl}" alt="${displayName}" /></div>`,
          className: 'marker-icon-container',
          iconSize: L.point(iconSize, iconSize),
          iconAnchor: [iconSize / 2, iconSize / 2],
          popupAnchor: [0, -(iconSize / 2)],
        })
      }

      marker.setIcon(this.markerIcons[poi.category])

      // Store marker references for layer control (markers only added to mainClusterGroup)
      if (!this.markerLayerGroups[poi.category]._markerRefs) {
        this.markerLayerGroups[poi.category]._markerRefs = []
      }
      this.markerLayerGroups[poi.category]._markerRefs.push(marker)

      const popup = createPopup({
        ImagePath: poi.thumbnailPath,
        Title: poi.name,
        Description: poi.popupDescription || poi.description || null,
        onButtonClick: () => {
          this.centerMapOnCurrentPoi(poi)
          this.onMarkerClick(poi.id, 'poi', poi)
        },
      })

      marker.bindPopup(popup, { minWidth: 240, maxWidth: 240 })

      marker.addEventListener('mouseover', function (event) {
        marker.openPopup()
      })

      marker.addEventListener('click', function (event) {
        const data = event.sourceTarget
        self.onMarkerClick(data.id, 'poi', data)
      })

      function closeMarkerPopup() {
        marker.closePopup()
      }

      // Delay popup close to allow moving between marker and popup without closing
      const timeoutDelay = 300

      marker.addEventListener('mouseout', function (event) {
        const closeMarkerPopupTimeout = setTimeout(closeMarkerPopup, timeoutDelay)
        popup.addEventListener('mouseover', () => clearTimeout(closeMarkerPopupTimeout))
      })

      popup.addEventListener('mouseleave', function (event) {
        const closeMarkerPopupTimeout = setTimeout(closeMarkerPopup, timeoutDelay)
        marker.addEventListener('mouseover', () => clearTimeout(closeMarkerPopupTimeout))
      })
    }

    // Clean up empty layer groups
    for (const property in this.markerLayerGroups) {
      if (!this.markerLayerGroups[property]._markerRefs || !this.markerLayerGroups[property]._markerRefs.length) {
        delete this.markerLayerGroups[property]
      }
    }

    this.setMarkerGroups()
    this.setMarkerLegend()

    // Mark markers as loaded
    this.markersLoaded = true
    this.checkIfAllDataLoaded()
  }

  /**
   * Sync route KML layers visibility based on marker layer visibility
   * Routes are shown when their corresponding marker category is visible
   */
  syncRouteLayers() {
    const routeCategoryMap = {
      Wandelroute: this.walkingRoutesKmlLayer,
      Fietsroute: this.cycleRoutesKmlLayer,
      Ruiterpad: this.ridersRoutesKmlLayer,
      'Rolstoel- en kinderwagenroute': this.wheelchairKmlLayer,
    }

    for (const [category, routeLayer] of Object.entries(routeCategoryMap)) {
      if (!routeLayer || !this.markerLayerGroups[category]) continue

      const isMarkerLayerVisible = this.map.hasLayer(this.markerLayerGroups[category])
      const isRouteLayerVisible = this.map.hasLayer(routeLayer)

      if (isMarkerLayerVisible && !isRouteLayerVisible) {
        this.map.addLayer(routeLayer)
      } else if (!isMarkerLayerVisible && isRouteLayerVisible) {
        this.map.removeLayer(routeLayer)
      }
    }
  }

  setMarkerGroups() {
    if (!!Object(this.markerLayerGroups).length) return

    const categories = this.params.getAll('category')

    // Toggle marker visibility in cluster group based on category selection
    const toggleCategoryMarkers = (category, show) => {
      if (!this.markerLayerGroups[category] || !this.markerLayerGroups[category]._markerRefs) return

      this.markerLayerGroups[category]._markerRefs.forEach((marker) => {
        if (show) {
          if (!this.mainClusterGroup.hasLayer(marker)) {
            this.mainClusterGroup.addLayer(marker)
          }
        } else {
          this.mainClusterGroup.removeLayer(marker)
        }
      })
    }

    if (categories.length && categories[0]) {
      for (const property in this.markerLayerGroups) {
        toggleCategoryMarkers(property, false)
      }

      this.params.getAll('category').forEach((property) => {
        if (property && this.markerLayerGroups[property]) {
          if (!this.map.hasLayer(this.markerLayerGroups[property])) {
            this.markerLayerGroups[property].addTo(this.map)
          }
          toggleCategoryMarkers(property, true)
        }
      })
    } else {
      for (const property in this.markerLayerGroups) {
        if (!this.map.hasLayer(this.markerLayerGroups[property])) {
          this.markerLayerGroups[property].addTo(this.map)
        }
        toggleCategoryMarkers(property, true)
      }
    }

    this.syncRouteLayers()

    this.mainClusterGroup.refreshClusters()
  }

  setMarkerLegend() {
    if (!!Object(this.markerLayerGroups).length || !this.settings.showMarkerLegend) return

    this.setResetControl()

    // Organize categories into groups for legend display
    const groupedCategories = {}
    const ungroupedCategories = []

    for (const groupName in CATEGORY_GROUPS) {
      groupedCategories[groupName] = []
      for (const categoryName of CATEGORY_GROUPS[groupName]) {
        if (this.markerLayerGroups.hasOwnProperty(categoryName)) {
          groupedCategories[groupName].push(categoryName)
        }
      }
      if (groupedCategories[groupName].length === 0) {
        delete groupedCategories[groupName]
      }
    }

    for (const key in this.markerLayerGroups) {
      if (Object.hasOwnProperty.call(this.markerLayerGroups, key)) {
        let foundInGroup = false
        for (const groupName in CATEGORY_GROUPS) {
          if (CATEGORY_GROUPS[groupName].includes(key)) {
            foundInGroup = true
            break
          }
        }
        if (!foundInGroup) {
          ungroupedCategories.push(key)
        }
      }
    }

    // Add ungrouped categories to "Overig" group
    if (ungroupedCategories.length > 0) {
      if (groupedCategories['Overig']) {
        groupedCategories['Overig'].push(...ungroupedCategories)
      } else {
        groupedCategories['Overig'] = ungroupedCategories
      }
    }

    const styledLayerGroups = {}
    const categoryOrder = []

    for (const groupName in groupedCategories) {
      for (const categoryName of groupedCategories[groupName]) {
        const element = this.markerLayerGroups[categoryName]
        let icon
        if (this.mapType == 'volunteerGroups') {
          // Use PNG for volunteer groups (standard), except Terreinen Brabants Landschap which uses SVG
          if (categoryName === 'Terreinen Brabants Landschap') {
            icon = `/_resources/ontdekdenatuurmodule/img/markers/${getFormattedFilename(categoryName)}-icon.svg`
          } else {
            icon = `/_resources/ontdekdenatuurmodule/img/markers/${getFormattedFilename(categoryName)}-icon.png`
          }
        } else {
          icon = `/_resources/ontdekdenatuurmodule/img/markers/${getFormattedFilename(categoryName)}.svg`
        }

        styledLayerGroups[
          `<div class="control-layer__item is--${getFormattedFilename(
            categoryName
          )}"><img class="icon" src="${icon}" alt="${getPluralName(categoryName)}" /><span>${getPluralName(
            categoryName
          )}</span></div>`
        ] = element
        categoryOrder.push({ categoryName, groupName })
      }
    }

    const layersControl = L.control.layers(null, styledLayerGroups, {
      collapsed: false,
    })

    layersControl.addTo(this.map)

    if (this.filtersContainerContent) {
      const layersElement = document.querySelector('.leaflet-control-layers')
      if (layersElement) {
        this.addControlToFiltersContainer(layersElement, 'last')

        // Store for re-adding titles after filter resets
        this.groupedCategories = groupedCategories
        this.categoryOrder = categoryOrder

        requestAnimationFrame(() => {
          this.addCategoryGroupTitles(layersElement, groupedCategories, categoryOrder)
        })
      }
    }

    // Sync marker visibility in cluster with layer control toggles
    this.map.addEventListener('overlayadd overlayremove', (e) => {
      const category = Object.keys(this.markerLayerGroups).find((key) => this.markerLayerGroups[key] === e.layer)

      if (category && this.markerLayerGroups[category]._markerRefs) {
        const isVisible = this.map.hasLayer(e.layer)
        this.markerLayerGroups[category]._markerRefs.forEach((marker) => {
          if (isVisible) {
            if (!this.mainClusterGroup.hasLayer(marker)) {
              this.mainClusterGroup.addLayer(marker)
            }
          } else {
            this.mainClusterGroup.removeLayer(marker)
          }
        })

        this.mainClusterGroup.refreshClusters()
      }

      this.setMarkerGroupParams()
    })
  }

  addCategoryGroupTitles(layersElement, groupedCategories, categoryOrder) {
    // Remove existing titles to prevent duplicates when called multiple times
    const existingTitles = layersElement.querySelectorAll('.control-layer__group-title')
    existingTitles.forEach((title) => title.remove())

    const labels = Array.from(layersElement.querySelectorAll('.leaflet-control-layers label'))

    if (labels.length === 0 || labels.length !== categoryOrder.length) return

    const addedGroups = new Set()
    let lastGroup = null

    categoryOrder.forEach(({ categoryName, groupName }, index) => {
      if (index < labels.length) {
        const label = labels[index]

        if (groupName !== lastGroup && !addedGroups.has(groupName)) {
          const groupTitle = document.createElement('div')
          groupTitle.classList.add('control-layer__group-title')
          groupTitle.textContent = groupName

          label.parentNode.insertBefore(groupTitle, label)
          addedGroups.add(groupName)
          lastGroup = groupName
        }
      }
    })
  }

  setRegionSearchField() {
    if (!this.settings.showRegionSearchControl || !this.regionArr) return

    const natureReserves = this.poiArr.reduce((acc, cur) => {
      if (cur.category === 'Natuurgebied') {
        acc.push(cur)
      }
      return acc
    }, [])

    const regionWithNatureReserves = this.regionArr.map((item) => {
      const container = {}

      const RegionNatureReserves = natureReserves.reduce((acc, cur) => {
        if (cur.regionId === item.id) {
          acc.push(cur)
        }
        return acc
      }, [])

      container.regionName = item.name
      container.regionId = item.id
      container.natureReserves = RegionNatureReserves

      return container
    })

    const self = this

    L.Control.RegionSearchField = L.Control.extend({
      onAdd: function (map) {
        const dropdownFilter = createDropdownFilter(regionWithNatureReserves, 'region-search-filter')
        new DropdownFilter(dropdownFilter)

        const input = dropdownFilter.querySelector('.js-dropdown-filter-result')

        input.addEventListener('change', () => {
          const region = self.getRegionById(input.value)

          if (region) {
            self.asideContainer.innerHTML = ''
            self.centerMapOnCurrentRegion(region)
          } else {
            const poi = self.getPoiById(input.value)

            if (poi) {
              self.centerMapOnCurrentPoi(poi)
              self.onMarkerClick(poi.id, 'poi', poi)
            }
          }
        })

        const resetButton = dropdownFilter.querySelector('.js-dropdown-filter-reset')
        resetButton.addEventListener('click', () => {
          self.map.fitBounds(self.regionsKmlLayer.getBounds(), { maxZoom: 10 })
          input.value = ''
          dropdownFilter.querySelector('.js-dropdown-filter-select').innerHTML = 'Zoek op regio of natuurgebied'
        })

        return dropdownFilter
      },
    })

    L.control.RegionSearchField = function (opts) {
      return new L.Control.RegionSearchField(opts)
    }

    const regionSearchControl = L.control.RegionSearchField()
    const regionSearchElement = regionSearchControl.onAdd(this.map)

    this.dropdownFilter = regionSearchElement

    const wrapper = document.createElement('div')
    wrapper.classList.add('leaflet-control')
    wrapper.appendChild(regionSearchElement)

    if (!this.addControlToFiltersContainer(wrapper, 'first')) {
      regionSearchControl.addTo(this.map)
    }

    const elem = L.DomUtil.get('region-search-filter')
    if (elem) {
      L.DomEvent.on(elem, 'mousewheel', L.DomEvent.stopPropagation)
      // Prevent touch events from propagating to the map when scrolling in dropdown
      L.DomEvent.on(elem, 'touchstart touchmove touchend', L.DomEvent.stopPropagation)
      
      // Also prevent touch events on the dropdown list itself
      const dropdownList = elem.querySelector('.js-dropdown-filter-dropdown')
      if (dropdownList) {
        L.DomEvent.on(dropdownList, 'touchstart touchmove touchend', L.DomEvent.stopPropagation)
        const listItems = dropdownList.querySelector('.js-dropdown-filter-items')
        if (listItems) {
          L.DomEvent.on(listItems, 'touchstart touchmove touchend', L.DomEvent.stopPropagation)
        }
      }
    }
  }

  setPlacesSelect() {
    if (!this.settings.showPlacesSlectControl) return

    const self = this

    L.Control.PlacesSelect = L.Control.extend({
      onAdd: function (map) {
        const select = document.createElement('select')
        select.classList.add('field__input')

        const placeholder = document.createElement('option')
        placeholder.value = ''
        placeholder.text = 'Selecteer plaats'
        select.add(placeholder, null)

        for (const [key, value] of Object.entries(places)) {
          const option = document.createElement('option')
          option.value = key
          option.text = key

          select.add(option, null)
        }

        select.addEventListener('change', (e) => self.goToPlace(e.target.value))

        return select
      },
    })

    L.control.PlacesSelect = function (opts) {
      return new L.Control.PlacesSelect(opts)
    }

    const placesSelectControl = L.control.PlacesSelect()
    const placesSelectElement = placesSelectControl.onAdd(this.map)

    const wrapper = document.createElement('div')
    wrapper.classList.add('leaflet-control')
    wrapper.appendChild(placesSelectElement)

    if (!this.addControlToFiltersContainer(wrapper, 'first')) {
      placesSelectControl.addTo(this.map)
    }
  }

  activeLegendCount() {
    const categoryNames = Object.keys(this.markerLayerGroups)

    return categoryNames.reduce((acc, name) => {
      return this.map.hasLayer(this.markerLayerGroups[name]) ? acc + 1 : acc
    }, 0)
  }

  toggleLegendReset() {
    if (this.activeLegendCount() < 2) {
      for (const property in this.markerLayerGroups) {
        this.markerLayerGroups[property].addTo(this.map)
        if (this.markerLayerGroups[property]._markerRefs) {
          this.markerLayerGroups[property]._markerRefs.forEach((marker) => {
            if (!this.mainClusterGroup.hasLayer(marker)) {
              this.mainClusterGroup.addLayer(marker)
            }
          })
        }
      }
    } else {
      for (const property in this.markerLayerGroups) {
        this.markerLayerGroups[property].remove()
        if (this.markerLayerGroups[property]._markerRefs) {
          this.markerLayerGroups[property]._markerRefs.forEach((marker) => {
            this.mainClusterGroup.removeLayer(marker)
          })
        }
      }
    }

    this.syncRouteLayers()
    this.mainClusterGroup.refreshClusters()
  }

  setResetControl() {
    const self = this

    L.Control.ResetButtons = L.Control.extend({
      onAdd: function (map) {
        const container = document.createElement('div')
        container.classList.add('reset-buttons-container')

        const showAllButton = document.createElement('button')
        showAllButton.classList.add('button-reset', 'button-reset--show-all')
        showAllButton.innerHTML =
          '<img src="/_resources/ontdekdenatuurmodule/img/leaflet/filter-eye.svg" alt="Filter eye" /> Toon alles'
        showAllButton.addEventListener('click', () => self.showAllLayers())

        const zoomOutButton = document.createElement('button')
        zoomOutButton.classList.add('button-reset', 'button-reset--zoom-out', 'hidden--gt-palm')
        zoomOutButton.innerHTML =
          '<img src="/_resources/ontdekdenatuurmodule/img/leaflet/fit-to-window.svg" alt="Zoom out" /> Uitzoomen'
        zoomOutButton.addEventListener('click', () => {
          // Close filter popup
          const filtersContainer = self.filtersContainer || document.querySelector('.filters-container')
          if (filtersContainer) {
            filtersContainer.setAttribute('data-expanded', 'false')
            const mapContainer = self.mapSection || map.getContainer().closest('.map') || map.getContainer()
            if (mapContainer) {
              mapContainer.classList.remove('filters-overlay-active')
            }
          }
          if (self.mobileFilterButton) {
            self.mobileFilterButton.setAttribute('data-active', 'false')
          }
          // Zoom out to show the whole province of Noord-Brabant
          const noordBrabantBounds = L.latLngBounds(
            [51.220901, 4.191824], // Southwest corner (south, west)
            [51.838769, 6.047897] // Northeast corner (north, east)
          )
          self.map.fitBounds(noordBrabantBounds, { maxZoom: 10 })
        })

        const resetFiltersButton = document.createElement('button')
        resetFiltersButton.classList.add('button-reset', 'button-reset--reset-filters', 'hidden--palm')
        resetFiltersButton.innerHTML =
          '<img src="/_resources/ontdekdenatuurmodule/img/leaflet/filter-close.svg" alt="Filter close" /> Filters wissen'
        resetFiltersButton.addEventListener('click', () => self.resetAllFilters())

        container.appendChild(showAllButton)
        container.appendChild(zoomOutButton)
        container.appendChild(resetFiltersButton)

        return container
      },
    })

    L.control.ResetButtons = function (opts) {
      return new L.Control.ResetButtons(opts)
    }

    const resetControl = L.control.ResetButtons()
    const resetElement = resetControl.onAdd(this.map)

    const wrapper = document.createElement('div')
    wrapper.classList.add('leaflet-control')
    wrapper.appendChild(resetElement)

    if (!this.addControlToFiltersContainer(wrapper, 'middle')) {
      resetControl.addTo(this.map)
    }
  }

  showAllLayers() {
    for (const property in this.markerLayerGroups) {
      if (!this.map.hasLayer(this.markerLayerGroups[property])) {
        this.markerLayerGroups[property].addTo(this.map)
        if (this.markerLayerGroups[property]._markerRefs) {
          this.markerLayerGroups[property]._markerRefs.forEach((marker) => {
            if (!this.mainClusterGroup.hasLayer(marker)) {
              this.mainClusterGroup.addLayer(marker)
            }
          })
        }
      }
    }
    this.syncRouteLayers()
    this.mainClusterGroup.refreshClusters()

    // Re-add group titles after showing layers (they get removed when DOM updates)
    if (this.groupedCategories && this.categoryOrder) {
      const layersElement = document.querySelector('.leaflet-control-layers')
      if (layersElement) {
        requestAnimationFrame(() => {
          this.addCategoryGroupTitles(layersElement, this.groupedCategories, this.categoryOrder)
        })
      }
    }
  }

  resetAllFilters() {
    if (this.dropdownFilter) {
      const input = this.dropdownFilter.querySelector('.js-dropdown-filter-result')
      const select = this.dropdownFilter.querySelector('.js-dropdown-filter-select')

      if (input) {
        input.value = ''
      }
      if (select) {
        select.innerHTML = 'Selecteer een regio of natuurgebied'
      }
    }

    for (const property in this.markerLayerGroups) {
      if (this.map.hasLayer(this.markerLayerGroups[property])) {
        this.markerLayerGroups[property].remove()
        if (this.markerLayerGroups[property]._markerRefs) {
          this.markerLayerGroups[property]._markerRefs.forEach((marker) => {
            this.mainClusterGroup.removeLayer(marker)
          })
        }
      }
    }
    this.syncRouteLayers()
    this.mainClusterGroup.refreshClusters()

    // Re-add group titles after reset (they get removed when layers are removed)
    if (this.groupedCategories && this.categoryOrder) {
      const layersElement = document.querySelector('.leaflet-control-layers')
      if (layersElement) {
        requestAnimationFrame(() => {
          this.addCategoryGroupTitles(layersElement, this.groupedCategories, this.categoryOrder)
        })
      }
    }
  }

  setUserLocationControl() {
    if (!this.settings.showUserLocationControl) return

    const self = this

    L.Control.userLocation = L.Control.extend({
      onAdd: function (map) {
        const button = document.createElement('button')
        button.classList.add('button-leaflet', 'button-leaflet--user-location')

        button.addEventListener('click', () => self.centerMapOnUserLocation())

        return button
      },
    })

    L.control.userLocation = function (opts) {
      return new L.Control.userLocation(opts)
    }

    L.control.userLocation({ position: 'topright' }).addTo(this.map)
  }

  getRegionData() {
    if (!shouldFetchRegions(this.mapSection)) return
    this.getRegionDataPromise = new Promise(
      function (resolve, reject) {
        fetch(`${this.feed}/regions`, {
          headers: {
            'x-requested-with': 'XMLHttpRequest',
          },
        })
          .then((response) => response.json())
          .then((data) => {
            this.regionArr = data
            this.regionsLoaded = true
            this.setRegionKmlLayer(!this.currentMarker && !this.params.get('omgeving'))

            let regionBasedOnParam = false
            let regionBasedOnLastSegment = false

            if (this.params.get('Regio')) {
              const found = this.regionArr.find((element) => element.slug === this.params.get('Regio'))
              regionBasedOnParam = found ? found : false
            }

            if (this.lastSegment) {
              const found = this.regionArr.find((element) => element.slug === this.lastSegment.replace(/\\/g, ''))
              regionBasedOnLastSegment = found ? found : false
            }

            if (regionBasedOnParam) {
              this.currentRegion = regionBasedOnParam
              this.params.delete('Regio')
            } else if (regionBasedOnLastSegment) {
              this.currentRegion = regionBasedOnLastSegment
              this.baseHref = this.baseHref.replace(`/${this.lastSegment}`, '')
            }

            if (this.currentRegion) {
              this.centerMapOnCurrentRegion(this.currentRegion)
            }

            this.checkIfAllDataLoaded()
            resolve(this.regionArr)
          })
          .catch((error) => {
            console.error('Error:', error)
            reject('error')
          })
      }.bind(this)
    )
  }

  getRegionById(id) {
    return this.regionArr.find((element) => element.id === parseInt(id, 10))
  }

  setRegionKmlLayer(fitBounds = true) {
    // Try dummy data first (for testing), fall back to real KML
    const dummyLayer = setupDummyRegionsLayer(this.regionArr, this, fitBounds)
    if (dummyLayer) {
      return
    }

    if (!this.mapSection.dataset.kmlRegions) return

    const kmlLayer = loadKml(this.mapSection.dataset.kmlRegions)
    kmlLayer.addTo(this.map)

    // Track KML loading
    const kmlPromise = new Promise((resolve) => {
      kmlLayer.on('ready', () => {
        if (fitBounds) {
          this.map.fitBounds(kmlLayer.getBounds(), { maxZoom: 10 })
        }

        kmlLayer.eachLayer((layer) => {
          const region = this.regionArr.find((obj) => {
            return parseInt(obj.kmlId, 10) === parseInt(layer.feature.properties.Id, 10)
          })

          const popup = createPopup({
            Title: region ? region.name : layer.feature.properties.Regio,
            onButtonClick: region
              ? () => {
                  this.asideContainer.innerHTML = ''
                  this.centerMapOnCurrentRegion(region)
                }
              : false,
          })

          layer.bindPopup(popup)

          layer.addEventListener('click', () => {
            this.asideContainer.innerHTML = ''
            this.setAside(region.id, 'region')
            this.currentRegion = region
            this.currentMarker = false
            this.updateURL()
          })
        })
        resolve()
      })
    })
    this.loadingPromises.push(kmlPromise)

    this.regionsKmlLayer = kmlLayer
  }

  setPropertiesKmlLayer() {
    if (!this.mapSection.dataset.kmlProperties) return

    const kmlLayer = loadKml(this.mapSection.dataset.kmlProperties, 0.4, false)
    kmlLayer.addTo(this.map)

    // Track KML loading
    const kmlPromise = new Promise((resolve) => {
      kmlLayer.on('ready', () => resolve())
    })
    this.loadingPromises.push(kmlPromise)

    this.propertiesKmlLayer = kmlLayer
  }

  setWalkingRoutesKmlLayer() {
    const kmlLayer = loadRouteKmlLayer(this.mapSection.dataset.kmlWalkingRoutes, this.map)
    if (kmlLayer) {
      const kmlPromise = new Promise((resolve) => {
        kmlLayer.on('ready', () => resolve())
      })
      this.loadingPromises.push(kmlPromise)
    }
    this.walkingRoutesKmlLayer = kmlLayer
  }

  setCyclingRoutesKmlLayer() {
    const kmlLayer = loadRouteKmlLayer(this.mapSection.dataset.kmlCycleRoutes, this.map)
    if (kmlLayer) {
      const kmlPromise = new Promise((resolve) => {
        kmlLayer.on('ready', () => resolve())
      })
      this.loadingPromises.push(kmlPromise)
    }
    this.cycleRoutesKmlLayer = kmlLayer
  }

  setWheelchairRoutesKmlLayer() {
    const kmlLayer = loadRouteKmlLayer(this.mapSection.dataset.kmlWheelchairRoutes, this.map)
    if (kmlLayer) {
      const kmlPromise = new Promise((resolve) => {
        kmlLayer.on('ready', () => resolve())
      })
      this.loadingPromises.push(kmlPromise)
    }
    this.wheelchairKmlLayer = kmlLayer
  }

  setRidersRoutesKmlLayer() {
    const kmlLayer = loadRouteKmlLayer(this.mapSection.dataset.kmlRiderRoutes, this.map)
    if (kmlLayer) {
      const kmlPromise = new Promise((resolve) => {
        kmlLayer.on('ready', () => resolve())
      })
      this.loadingPromises.push(kmlPromise)
    }
    this.ridersRoutesKmlLayer = kmlLayer
  }

  centerMap(center = this.center, zoom = this.zoom, popupContentString = false) {
    this.map.setView(center, zoom)

    if (popupContentString) {
      const popup = createPopup({ Description: popupContentString })

      L.marker(center).addTo(this.map).bindPopup(popup).openPopup()
    }
  }

  centerMapOnCurrentPoi(poi) {
    this.map.setView([poi.lat, poi.lng], 15)
  }

  centerMapOnCurrentRegion(region) {
    this.currentRegion = region
    this.currentMarker = false

    this.map.setView([region.lat, region.lng], 12)
    this.setAside(region.id, 'region')

    this.tourmakeCta(region)

    this.updateURL()
  }

  tourmakeCta(region) {
    if (!region.image || !region.tourmake) {
      if (this.tourmakeButton) {
        this.tourmakeButton.classList.add('hidden')
      }

      return
    } else if (this.tourmakeButton) {
      this.tourmakeButton.classList.remove('hidden')

      this.tourmakeButton.querySelector('.button-leaflet__image > img').src = region.image
      this.tourmakeButton.querySelector('.button-leaflet__label').innerHTML = region.name

      this.tourmakeButton.dataset.src = region.tourmake

      return
    }

    const self = this

    L.Control.tourmakeCta = L.Control.extend({
      onAdd: function (map) {
        const holder = document.createElement('div')
        holder.classList.add('button-leaflet--tourmake-holder')
        holder.dataset.state = 'visible'

        const button = document.createElement('button')
        button.classList.add('js-tourmake-button', 'button-leaflet--tourmake', 'button-leaflet')
        button.dataset.src = region.tourmake

        const label = document.createElement('div')
        label.classList.add('button-leaflet__label')
        label.innerHTML = region.name

        const imageHolder = document.createElement('div')
        imageHolder.classList.add('button-leaflet__image')

        const image = document.createElement('img')
        image.src = region.image

        imageHolder.appendChild(image)

        button.appendChild(imageHolder)
        button.appendChild(label)

        button.addEventListener('click', () => {
          self.closeAside()

          Fancybox.show([
            {
              src: button.dataset.src,
              type: 'iframe',
            },
          ])
        })

        const toggleButton = document.createElement('button')
        toggleButton.classList.add('button-leaflet', 'button-leaflet--tourmake-toggle')
        toggleButton.innerHTML = 'Sluiten'

        toggleButton.addEventListener('click', () => {
          if (holder.dataset.state === 'visible') {
            holder.dataset.state = 'hidden'
            toggleButton.innerHTML = 'Toon 360° view'
          } else {
            holder.dataset.state = 'visible'
            toggleButton.innerHTML = 'Sluiten'
          }
        })

        holder.appendChild(button)
        holder.appendChild(toggleButton)

        self.tourmakeButton = holder

        return holder
      },
    })

    L.control.tourmakeCta = function (opts) {
      return new L.Control.tourmakeCta(opts)
    }

    L.control.tourmakeCta({ position: 'bottomright' }).addTo(this.map)
  }

  centerMapOnUserLocation() {
    const self = this

    if (this.userLocation) {
      this.centerMap(this.userLocation.latlng, 14)
      // Delay popup to ensure map view has finished animating
      setTimeout(function () {
        self.userLocationMarker.openPopup()
      }, 500)
      return
    }

    this.map.locate({ setView: true, maxZoom: 14 })

    function onLocationFound(e) {
      var radius = e.accuracy

      const popup = createPopup({
        Description: `Je bent binnen ${parseFloat(radius).toFixed(2)} meter van dit punt`,
      })

      self.userLocationMarker = L.marker(e.latlng).bindPopup(popup).addTo(self.map)
      self.userLocationCircle = L.circle(e.latlng, radius).addTo(self.map)

      self.userLocationMarker.openPopup()

      self.userLocation = e
    }

    function onLocationError(e) {
      self.map.setView(self.center, self.zoom)
    }

    this.map.addEventListener('locationfound', onLocationFound)
    this.map.addEventListener('locationerror', onLocationError)
  }

  setMarkerGroupParams() {
    if (!!Object(this.markerLayerGroups).length) return

    let activeMarkerLayerGroups = []

    for (const key in this.markerLayerGroups) {
      if (Object.hasOwnProperty.call(this.markerLayerGroups, key)) {
        const element = this.markerLayerGroups[key]

        if (this.map.hasLayer(element)) {
          activeMarkerLayerGroups.push(key)
        }
      }
    }

    this.syncRouteLayers()

    this.params.delete('category')

    // Only add category params if not all categories are active (cleaner URLs)
    if (activeMarkerLayerGroups.length < Object.keys(this.markerLayerGroups).length) {
      activeMarkerLayerGroups.forEach((item) => {
        this.params.append('category', item)
      })
    }

    this.updateURL()
  }

  goToPlace(place) {
    // Disable dragging temporarily to prevent Safari drag bug
    this.map.dragging.disable()

    if (place) {
      const placeObj = places[place]
      this.centerMap([placeObj.lat, placeObj.lng], 12, `Je bent in de omgeving van ${place}`)
      this.params.set('omgeving', place)
    } else {
      this.centerMap(this.center, this.zoom)
      this.params.delate('omgeving')
    }
    this.updateURL()

    this.map.dragging.enable()
  }

  onMarkerClick(id, type, obj) {
    if (!id || !this.asideContainer) return

    this.asideContainer.innerHTML = ''

    if (obj.category === '360° view') {
      this.closeAside()

      Fancybox.show([
        {
          src: obj.tour360Link,
        },
      ])

      this.currentMarker = obj
      this.updateURL()

      return
    }

    if (this.asideCurrent == id) {
      this.closeAside()
    } else {
      this.setAside(id, type)
      this.currentMarker = obj
      this.updateURL()
    }
  }

  updateURL() {
    let url = this.baseHref

    if (this.currentMarker) {
      url += url.slice(-1) === '/' ? this.currentMarker.slug : `/${this.currentMarker.slug}`
    } else if (this.currentRegion) {
      url += url.slice(-1) === '/' ? this.currentRegion.slug : `/${this.currentRegion.slug}`
    }

    if (this.params.toString().length) {
      url += `?${this.params.toString()}`
    }

    window.history.pushState(null, '', url)
  }

  closeAside() {
    this.asideContainer.innerHTML = ''

    const isMobile = window.matchMedia('(max-width: 480px)').matches
    const animationProp = isMobile ? { y: '100%' } : { x: '-100%' }
    gsap.to(this.aside, animationProp)
    gsap.to(this.asideCloseButton, { opacity: 0, x: 0 })

    this.asideCurrent = false
  }

  setAside(id, type) {
    if (!id || !this.asideContainer) return

    const self = this

    fetch(`${this.feed}/markerContent?${new URLSearchParams({ id, type })}`, {
      headers: {
        'x-requested-with': 'XMLHttpRequest',
      },
    })
      .then(function (response) {
        return response.text()
      })
      .then(function (html) {
        const parser = new DOMParser()
        const doc = parser.parseFromString(html, 'text/html')

        const tabButtons = doc.body.firstChild.querySelectorAll('.js-tab-nav button')
        const tabs = doc.body.firstChild.querySelectorAll('.js-tab')

        if (tabButtons.length && tabs.length) {
          tabButtons.forEach((button) => {
            const tabId = button.dataset.tabId
            button.addEventListener('click', () => {
              button.classList.add('is--active')

              tabButtons.forEach((element) => {
                if (element.dataset.tabId !== tabId) {
                  element.classList.remove('is--active')
                }
              })

              tabs.forEach((tab) => {
                if (tab.dataset.tabId === tabId) {
                  tab.style.display = 'block'
                } else {
                  tab.style.display = 'none'
                }
              })
            })
          })
        }

        if (doc.body.firstChild) {
          self.asideContainer.append(doc.body.firstChild)
        }

        if (!self.asideCurrent) {
          const isMobile = window.matchMedia('(max-width: 480px)').matches
          const animationProp = isMobile ? { y: 0 } : { x: 0 }
          gsap.to(self.aside, animationProp)
          gsap.to(self.asideCloseButton, { opacity: 1, x: '-50%' })
        }

        self.asideCurrent = id
      })
      .catch((error) => {
        console.error('Error:', error)
      })
  }
}

window.mapApp = new MapApp()
